// src/App.jsx
import React, { useEffect } from "react";
import { Routes, Route, Link, Navigate, useLocation } from "react-router-dom";

import Login from "./pages/Login";
import Products from "./pages/Products";
import Brands from "./pages/Brands";
import Categories from "./pages/Categories";
import NosCategories from "./pages/NosCategories";
import Dashboard from "./pages/Dashboard";
import HomePublic from "./pages/HomePublic";
import Catalogue from "./pages/Catalogue";
import ProductDetails from "./pages/ProductDetails";
import Cart from "./pages/Cart";
import ProtectedRoute from "./components/ProtectedRoute";
import { logout } from "./services/authService";
import PublicNavbar from "./components/PublicNavbar";
import { CartProvider } from "./context/CartContext";
import HeroEditor from "./pages/HeroEditor";
import HomeEditor from "./pages/HomeEditor";
import Checkout from "./pages/Checkout";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import SchemaExplorer from "./pages/SchemaExplorer";
import Footer from "./components/Footer";
import OrderLookupByPhone from "./pages/OrderLookupByPhone";
import AdminPromoList from "./pages/AdminPromoList";
import AdminPromoEditor from "./pages/AdminPromoEditor";
import PromoPage from "./pages/PromoPage";
import AdminSearchIndex from "./pages/AdminSearchIndex";
import ReceptionCreate from "./pages/ReceptionCreate";
import StockOverview from "./pages/StockOverview";
import ProductStockSheet from "./pages/ProductStockSheet";
import AdminOrders from "./pages/AdminOrders";
// ✅ toasts globaux (export par défaut)
import ToastProvider from "./components/ToastProvider";
import AdminHomeBanners from "./pages/AdminHomeBanners";

/* --------- Admin navbar --------- */
function AdminNavbar() {
  return (
    <nav className="navbar navbar-expand-lg bg-body-tertiary">
      <div className="container">
        <Link className="navbar-brand" to="/">Ecommerce Admin</Link>
        <div className="d-flex flex-wrap gap-2">
          <Link className="btn btn-sm btn-outline-secondary" to="/admin/dashboard">Dashboard</Link>
          <Link className="btn btn-sm btn-outline-secondary" to="/admin/products">Produits</Link>
          <Link className="btn btn-sm btn-outline-secondary" to="/admin/brands">Marques</Link>
          <Link className="btn btn-sm btn-outline-secondary" to="/admin/categories">Catégories</Link>
          <Link className="btn btn-sm btn-outline-secondary" to="/admin/hero">Bannières</Link>
          <Link className="btn btn-sm btn-outline-secondary" to="/admin/home-banners">Bannières Accueil</Link>
          <Link className="btn btn-sm btn-outline-secondary" to="/admin/promos">Promotions</Link>
          {/* <Link to="/admin/search-index" className="dropdown-item">Rebuild recherche</Link> */}
          <Link className="btn btn-sm btn-outline-secondary" to="/admin/home">Accueil</Link>
          <button className="btn btn-sm btn-outline-danger" onClick={logout}>Déconnexion</button>
        </div>
      </div>
    </nav>
  );
}

/* --------- ScrollToTop on route change --------- */
/* --------- Scroll: haut de page + support #hash --------- */
function ScrollRestorer() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // Si on a un hash (#new), on essaie de scroller vers l’élément
    if (hash) {
      const id = hash.slice(1);
      // petit délai pour laisser le DOM peindre
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          // optionnel: focus pour accessibilité
          if (typeof el.focus === "function") el.focus({ preventScroll: true });
          return;
        }
        // si pas trouvé, fallback top
        window.scrollTo({ top: 0, behavior: "auto" });
      }, 0);
      return () => clearTimeout(t);
    }

    // Pas de hash → on remonte tout en haut
    const main = document.getElementById("main-content");
    if (main) main.scrollIntoView({ behavior: "auto", block: "start" });
    else window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname, hash]);

  return null;
}


/* --------- Layout global --------- */
function Layout() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  // const hideFloatingOn = ["/cart", "/checkout", "/admin"]; // pages où on masque le panier flottant

  return (
    // Wrapper pleine hauteur pour sticky footer
    <div className="min-vh-100 d-flex flex-column">
      {/* Lien d'évitement (accessibilité) */}
      <a
        href="#main-content"
        className="visually-hidden-focusable position-absolute m-2 px-3 py-2 bg-dark text-white rounded"
        style={{ zIndex: 1080 }}
      >
        Aller au contenu
      </a>

      {/* Barre de navigation */}
      {isAdmin ? <AdminNavbar /> : <PublicNavbar />}

      {/* Contenu principal (prend tout l'espace restant) */}
      <main className="flex-fill" tabIndex={-1}>
        <Routes>
          {/* Public */}
          <Route path="/" element={<HomePublic />} />
          <Route path="/catalogue" element={<Catalogue />} />
          <Route path="/NosCategories" element={<NosCategories />} />
          <Route path="/p/:slug" element={<ProductDetails />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/login" element={<Login />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/schema" element={<SchemaExplorer />} />
          <Route path="/orders/lookup-phone" element={<OrderLookupByPhone />} />
          <Route path="/promos" element={<PromoPage />} />

          {/* Admin */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute mustBeAdmin>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/products"
            element={
              <ProtectedRoute mustBeAdmin>
                <Products />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/brands"
            element={
              <ProtectedRoute mustBeAdmin>
                <Brands />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/categories"
            element={
              <ProtectedRoute mustBeAdmin>
                <Categories />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/hero"
            element={
              <ProtectedRoute mustBeAdmin>
                <HeroEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/home"
            element={
              <ProtectedRoute mustBeAdmin>
                <HomeEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/promos"
            element={<ProtectedRoute mustBeAdmin><AdminPromoList /></ProtectedRoute>}
          />
          <Route
            path="/admin/promos/:id"
            element={<ProtectedRoute mustBeAdmin><AdminPromoEditor /></ProtectedRoute>}
          />
          <Route
            path="/admin/promos/new"
            element={<ProtectedRoute mustBeAdmin><AdminPromoEditor isNew /></ProtectedRoute>}
          />
          <Route path="/admin/home-banners" element={<AdminHomeBanners />} />

          <Route path="/admin/search-index" element={<AdminSearchIndex />} />
          <Route path="/admin/receptions/new" element={<ReceptionCreate />} />
          <Route path="/admin/stock" element={<StockOverview />} />
          <Route path="/admin/stock/:productId" element={<ProductStockSheet />} />
          <Route path="/admin/orders" element={<AdminOrders />} />
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {/* Footer (reste en bas grâce au wrapper flex) */}
      {!isAdmin && <Footer />}

      {/* Panier flottant public */}
      {/* {!isAdmin && <FloatingCart hideOn={hideFloatingOn} />} */}

      {/* Auto scroll au changement de route */}
      <ScrollRestorer />
    </div>
  );
}
//console.log(getApiBase())
/* --------- App root --------- */
export default function App() {
  return (

    <ToastProvider>
      <CartProvider>
        <Layout />
      </CartProvider>
    </ToastProvider>
  );
}
